import './style.scss'
import React from 'react'
import { Card } from 'src/componentv2/Base/Card'
import { Badge } from 'src/componentv2/Base/Badge'
import { ProgressBar } from 'src/componentv2/Base/ProgressBar'
import { StepCard, StepCardProps } from 'src/componentv2/Base/StepCard'
import { Box, Flex, Text, tokens, UIProps } from 'src/componentv2/Base/ui'
import { Button } from 'src/componentv2/Base/Button'
import ellipseOne from 'src/assets/v2/img/ellipse_one.svg'
import ellipseTwo from 'src/assets/v2/img/ellipse_two.svg'

export interface ProgressInfo {
    value: number
    total: number
}

export interface ResourceItem {
    label: React.ReactNode
    icon?: React.ReactNode
    onClick?: () => void
}

export interface GetStartedProps extends UIProps {
    title: React.ReactNode
    subtitle?: React.ReactNode
    progress?: ProgressInfo
    steps: StepCardProps[]
    resourcesTitle?: React.ReactNode
    resources?: ResourceItem[]
    bannerBg?: string
}

export const GetStarted: React.FC<GetStartedProps> = ({
                                                          title,
                                                          subtitle,
                                                          progress,
                                                          steps,
                                                          resourcesTitle = 'Resources',
                                                          resources,
                                                          bannerBg = tokens.primarySoft,
                                                          bgColor = tokens.pageBg,
                                                          padding = '3.2',
                                                          className = '',
                                                          ...rest
                                                      }) => {
    const pct =
        progress && progress.total > 0
            ? Math.round((progress.value / progress.total) * 100)
            : 0

    return (
        <Box
            className={`getstarted ${className}`}
            bgColor={bgColor}
            padding={padding}
            {...rest}
        >
            <Text
                fontSize="3"
                style={{
                    fontFamily: 'Gilmer Bold',
                    fontWeight: 700,
                }}
            >
                {title}
            </Text>

            {(subtitle || progress) && (
                <Card
                    className="getstarted__banner"
                    topMargin="2"
                    bgColor={bannerBg}
                    borderColor="transparent"
                    padding="1.4"
                >
                    {subtitle && (
                        <Text
                            fontSize="1.4"
                            color={tokens.textDark}
                            style={{
                                fontFamily: 'Gilmer Regular',
                                fontWeight: 400,
                            }}
                        >
                            {subtitle}
                        </Text>
                    )}

                    {progress && (
                        <>
                            <Text
                                className="getstarted__completed"
                                topMargin="0.8"
                                fontSize="1.4"
                            >
                                {progress.value} of {progress.total} completed
                            </Text>

                            <Flex
                                topMargin="0.5"
                                gap="1"
                                align="center"
                            >
                                <ProgressBar
                                    value={pct}
                                    width="28"
                                    height="0.6"
                                />

                                <Text
                                    fontSize="1.3"
                                    color={tokens.textMuted}
                                    style={{
                                        fontFamily: 'Gilmer Regular',
                                        fontWeight: 400,
                                    }}
                                >
                                    {pct}%
                                </Text>
                            </Flex>
                        </>
                    )}

                    <img
                        src={ellipseOne}
                        alt=""
                        className="getstarted__ellipse getstarted__ellipse--one"
                    />

                    <img
                        src={ellipseTwo}
                        alt=""
                        className="getstarted__ellipse getstarted__ellipse--two"
                    />
                </Card>
            )}

            <div className="getstarted__grid">
                {steps.map((step, i) => (
                    <StepCard key={i} {...step} />
                ))}
            </div>

            {resources && resources.length > 0 && (
                <Card
                    className="getstarted__resources"
                    topMargin="2"
                    padding="1.6"
                >
                    <Flex gap="1.2" wrap="wrap">
                        <Text
                            className="getstarted__resources-title"
                            fontSize="1.5"
                        >
                            {resourcesTitle}
                        </Text>

                        {resources.map((r, i) => (
                            <Button
                                key={i}
                                variant="ghost"
                                size="sm"
                                icon={r.icon}
                                iconPosition="left"
                                onClick={r.onClick}
                                style={{
                                    fontFamily: 'Gilmer Medium',
                                    fontWeight: 500,
                                    minHeight: '3.6rem',
                                    padding: '0 1.4rem',
                                }}
                            >
                                {r.label}
                            </Button>
                        ))}
                    </Flex>
                </Card>
            )}
        </Box>
    )
}

export default GetStarted